import { observer } from "mobx-react-lite";
import { toJS } from "mobx";

import { FunctionDialog } from "@/components/FunctionsModal";
import { ChatFunctionCall, Function_, FunctionsType, Tool, ToolChoice } from "@/types";
import { FunctionDialogTriggerVariant } from "@/components/FunctionsModal/Types";
import { usePlayground } from "@/components/Playground/playground-store";
import { isBuiltInTool, isMcpTool, isToolVariable } from "@/utils/utils";

const Functions = observer(
  ({
    readonly = false,
    triggerVariant,
  }: {
    readonly?: boolean;
    triggerVariant?: FunctionDialogTriggerVariant;
  }) => {
    const playground = usePlayground();
    if (playground.mode === "completion") {
      return null;
    }

    const functionsType = playground.functionType;

    const functions = toJS(
      (functionsType === "tools" ? playground.tools : playground.functions) ?? [],
    );

    const functionCall = playground.functionCall ?? undefined;
    const toolChoice = playground.toolChoice ?? undefined;

    // Wrap a custom function into a function tool, but pass through tools that are
    // already tools (built-in tools, tool variables, registry references). Without
    // these guards, built-in tools (e.g. OpenRouter server tools) get flattened into
    // `{ type: "function", function: <builtInTool> }` and are sent as custom functions.
    const toToolShape = (func: Function_): Tool => {
      if (isToolVariable(func as Tool)) {
        return func as Tool;
      }
      if (isBuiltInTool(func) || isMcpTool(func)) {
        return func as Tool;
      }
      if ("type" in func && (func as Record<string, unknown>).type === "registry") {
        return func as Tool;
      }
      return {
        type: "function",
        function: func,
      };
    };

    const setFunctionsType = (newFunctionsType: FunctionsType) => {
      if (newFunctionsType === "tools") {
        const tools = functions.map(toToolShape);
        playground.updateTools(tools);
        if (functionCall) {
          playground.updateToolChoice(
            typeof functionCall === "string"
              ? functionCall
              : {
                  type: "function",
                  function: functionCall,
                },
          );
        }
        playground.updateFunctions(undefined);
        playground.updateFunctionCall(undefined);
      } else {
        playground.updateFunctions(functions);
        if (toolChoice) {
          playground.updateFunctionCall(
            typeof toolChoice === "string"
              ? toolChoice
              : {
                  name: toolChoice.function.name,
                },
          );
        }
        playground.updateTools(undefined);
        playground.updateToolChoice(undefined);
      }
    };

    const setFunctions = (newFunctions: Function_[]) => {
      if (functionsType === "tools") {
        const tools = newFunctions.map<Tool>(toToolShape);
        playground.updateTools(tools);
      } else {
        playground.updateFunctions(newFunctions);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onSubmit = (newFunction: any) => {
      let prev = JSON.parse(JSON.stringify(functions));
      const index = prev.findIndex(
        (func: Function_) => "name" in func && func.name === newFunction.name,
      );
      if (index !== -1) {
        prev[index] = newFunction;
      } else {
        prev = prev.concat([newFunction]);
      }

      setFunctions(prev);
    };

    const setFunctionCall = (newFunctionCall: ChatFunctionCall) => {
      if (functionsType === "tools") {
        const tool_choice: ToolChoice =
          typeof newFunctionCall === "string"
            ? newFunctionCall
            : {
                type: "function",
                function: newFunctionCall,
              };
        playground.updateToolChoice(tool_choice);
      } else {
        playground.updateFunctionCall(newFunctionCall);
      }
    };

    return (
      <FunctionDialog
        functions={functions}
        functionCall={functionsType === "functions" ? functionCall : undefined}
        toolChoice={functionsType === "tools" ? toolChoice : undefined}
        functionsType={functionsType}
        onSubmit={onSubmit}
        setFunctionCall={setFunctionCall}
        setFunctions={setFunctions}
        setFunctionsType={setFunctionsType}
        readonly={readonly}
        triggerVariant={triggerVariant}
      />
    );
  },
);

export default Functions;
